import { Spinner, SpinnerSize } from '@trarc/pc-shared';
import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Location, useNavigate } from 'react-router-dom';

import { axiosClient } from '../axios';
import { PATHS } from '../constants';
import { DocumentCategory, Scalars, useCreateDocumentMutation } from '../types/api.graphql';

export const useGenerateRiskReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [createDocument] = useCreateDocumentMutation();

  const handleDocumentCreation = async (organizationId: Scalars['ID']): Promise<Scalars['ID']> => {
    const oneYearFromToday = new Date();
    oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

    const response = await createDocument({
      variables: {
        documentParams: {
          organizationIds: [organizationId],
          category: DocumentCategory.RiskReport,
          documentUrl: '',
          expiresAt: oneYearFromToday.toISOString(),
          title: t('riskReport'),
        },
      },
    });

    return response?.data?.createDocument?.id;
  };

  const generateRiskReport = async (selectedOrgId: Scalars['ID'], originLocation?: Location) => {
    const riskReportDocumentId = await handleDocumentCreation(selectedOrgId);

    await axiosClient.get(`riskReport-pdf/${selectedOrgId}/${riskReportDocumentId}`, {
      responseType: 'arraybuffer',
    });

    navigate(PATHS.Documents.Uploads.Details(riskReportDocumentId), {
      state: originLocation ? { from: originLocation } : undefined,
    });
  };

  const generateRiskReportWithToast = async (
    selectedOrgId: Scalars['ID'],
    dealershipName?: string,
    originLocation?: Location,
  ): Promise<void> => {
    const toastId = toast.loading(t('riskRegistry.generateReport.loadingMessage', { name: dealershipName }), {
      icon: <Spinner size={SpinnerSize.md} />,
      style: { gap: '2rem' },
    });
    try {
      await generateRiskReport(selectedOrgId, originLocation);
      toast.dismiss(toastId);
    } catch (error) {
      toast.error(t('riskRegistry.generateReport.errorMessage', { error: String(error) }), { id: toastId });
    }
  };

  return { generateRiskReport, generateRiskReportWithToast };
};
